import { useState } from 'react';
import {
  Control,
  FormState,
  UseFormSetValue,
  UseFormClearErrors,
  Controller,
  useFieldArray,
  useWatch,
  FieldPath,
} from 'react-hook-form';
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import * as Yup from 'yup';
import { HERE_APIKEY } from '@constants';
import {
  CustomerFormValues,
  emptyCustomerContact,
  emptyCustomerTypedLocation,
} from './types';
import { Input } from '../../tailwindComponents/Input';
import { Timepicker } from '../../tailwindComponents/Timepicker';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { Select } from '../../tailwindComponents/Select';
import { CollapsibleFormSection } from '../../tailwindComponents/CollapsibleFormSection';

const iso2Rule = Yup.string().matches(/^[A-Za-z]{2}$/, {
  message: 'Use a 2-letter ISO country code',
  excludeEmptyString: true,
});
const iso3Rule = Yup.string().matches(/^[A-Za-z]{3}$/, {
  message: 'Use a 3-letter ISO currency code',
  excludeEmptyString: true,
});
const decimalRule = Yup.string().matches(/^\d+(\.\d+)?$/, {
  message: 'Enter a number',
  excludeEmptyString: true,
});
const intRule = Yup.string().matches(/^\d+$/, {
  message: 'Enter a whole number',
  excludeEmptyString: true,
});
const optionalEmailRule = Yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .email('Invalid email');

const contactRoleOptions = [
  { label: 'Primary', value: 'Primary' },
  { label: 'Billing', value: 'Billing' },
  { label: 'Operations', value: 'Operations' },
  { label: 'Logistics', value: 'Logistics' },
  { label: 'Finance', value: 'Finance' },
  { label: 'Legal', value: 'legal' },
  { label: 'Other', value: 'Other' },
];

const locationTypeOptions = [
  { label: 'Registered', value: 'registered' },
  { label: 'Billing', value: 'billing' },
  { label: 'Shipping', value: 'shipping' },
  { label: 'Headquarters', value: 'headquarters' },
];

export const customerFormSchema = Yup.object().shape({
  customerId: Yup.string().required('Customer ID is required'),
  name: Yup.string()
    .required('Customer name is required')
    .min(3, 'Customer name must be at least 3 characters'),
  type: Yup.string().required('Customer type is required'),
  contactName: Yup.string().required('Contact name is required'),
  contactEmail: Yup.string()
    .email('Invalid email')
    .required('Contact email is required'),
  contactNumber: Yup.string(),
  hqId: Yup.string(),
  hqName: Yup.string(),
  address: Yup.string().required('Location address is required'),
  country: Yup.string(),
  postcode: Yup.string(),
  location: Yup.object().shape({
    lon: Yup.number(),
    lat: Yup.number(),
  }),
  operatingHoursFrom: Yup.string().required('Operating hours from is required'),
  operatingHoursTo: Yup.string().required('Operating hours to is required'),
  notes: Yup.string(),
  legalEntity: Yup.object().shape({
    legalName: Yup.string(),
    tradingName: Yup.string(),
    companyRegistrationNumber: Yup.string(),
    vatNumber: Yup.string(),
    taxId: Yup.string(),
    jurisdictionCountry: iso2Rule,
    legalEntityType: Yup.string(),
    incorporationDate: Yup.string(),
  }),
  contacts: Yup.array().of(
    Yup.object().shape({
      contactId: Yup.string(),
      name: Yup.string(),
      email: optionalEmailRule,
      phoneCountryCode: Yup.string(),
      phoneNumber: Yup.string(),
      role: Yup.string(),
      locationId: Yup.string(),
      isActive: Yup.boolean(),
    })
  ),
  locations: Yup.array().of(
    Yup.object().shape({
      locationId: Yup.string(),
      type: Yup.string(),
      line1: Yup.string().max(200, 'Address line 1 must be 200 characters or fewer'),
      line2: Yup.string().max(200, 'Address line 2 must be 200 characters or fewer'),
      city: Yup.string(),
      county: Yup.string(),
      postalCode: Yup.string(),
      country: Yup.string(),
      coordinates: Yup.object().shape({
        lon: Yup.number(),
        lat: Yup.number(),
      }),
      isPrimary: Yup.boolean(),
      isActive: Yup.boolean(),
    })
  ),
  corporateHierarchy: Yup.object().shape({
    parentCustomerId: Yup.string(),
    ultimateParentCustomerId: Yup.string(),
    hierarchyLevel: intRule,
    regionalGrouping: Yup.string(),
    isGroupAccount: Yup.boolean(),
  }),
  commercial: Yup.object().shape({
    paymentTerms: Yup.string(),
    creditLimit: decimalRule,
    creditCurrency: iso3Rule,
    contractType: Yup.string(),
    defaultIncoterms: Yup.string(),
    pricingTier: Yup.string(),
  }),
  compliance: Yup.object().shape({
    kycStatus: Yup.string(),
    kycVerifiedAt: Yup.string(),
    sanctionsCheckStatus: Yup.string(),
    sanctionsCheckedAt: Yup.string(),
  }),
  recordType: Yup.string(),
  market: Yup.string(),
  channel: Yup.string(),
  lifecycleStatus: Yup.string(),
  partnerPayerRef: Yup.string(),
  partnerBillToRef: Yup.string(),
  readinessGaps: intRule,
  hierarchyGroupName: Yup.string(),
  hierarchyParentName: Yup.string(),
  hierarchySubNodeName: Yup.string(),
  hierarchyBanner: Yup.string(),
  hierarchyBucket: Yup.string(),
  hierarchyIsShipToSite: Yup.boolean(),
  hierarchyIsDemandNode: Yup.boolean(),
});

const recordTypeOptions = [
  { label: 'Sold-to (Customer)', value: 'Customer' },
  { label: 'Site', value: 'Site' },
  { label: 'Grouping node', value: 'GroupingNode' },
  { label: 'Aggregate', value: 'Aggregate' },
];

const lifecycleStatusOptions = [
  { label: 'Prospect', value: 'prospect' },
  { label: 'Onboarding', value: 'onboarding' },
  { label: 'Active', value: 'active' },
  { label: 'On hold', value: 'onHold' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Archived', value: 'archived' },
];

const hierarchyBucketOptions = [
  { label: 'Group (files under a shared account header)', value: 'group' },
  { label: 'Independent (standalone account)', value: 'independent' },
];

function GroupTextField({
  control,
  name,
  label,
  optional = true,
  type = 'text',
  placeholder,
}: {
  control: Control<CustomerFormValues>;
  name: FieldPath<CustomerFormValues>;
  label: string;
  optional?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          value={(field.value as string) ?? ''}
          label={label}
          type={type}
          placeholder={placeholder}
          optional={optional}
        />
      )}
    />
  );
}

function GroupCheckbox({
  control,
  name,
  label,
}: {
  control: Control<CustomerFormValues>;
  name: FieldPath<CustomerFormValues>;
  label: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <label className='flex items-center gap-2 font-inter text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={!!field.value}
            onChange={(event) => field.onChange(event.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
          />
          {label}
        </label>
      )}
    />
  );
}

interface CustomerFormFieldsProps {
  control: Control<CustomerFormValues>;
  formState: FormState<CustomerFormValues>;
  setValue: UseFormSetValue<CustomerFormValues>;
  clearErrors: UseFormClearErrors<CustomerFormValues>;
}

export function CustomerFormFields({
  control,
  formState,
  setValue,
  clearErrors,
}: CustomerFormFieldsProps) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [locationOptionsMap, setLocationOptionsMap] = useState<
    Record<number, any[]>
  >({});
  const [locationInputMap, setLocationInputMap] = useState<
    Record<number, string | null>
  >({});

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({ control, name: 'contacts' });

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: 'locations' });

  const watchedLocations =
    useWatch({ control, name: 'locations' }) ?? [];

  const linkedLocationOptions = [
    { label: '— None —', value: '' },
    ...watchedLocations.map((location, index) => ({
      label: location?.line1 || location?.city || `Location ${index + 1}`,
      value: location?.locationId || '',
    })),
  ];

  const handleFetchLocationSuggestions = (input: string) => {
    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: any;
              address: { label: string; city: string; countryName: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setOptions(items);
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleLocationOptionSelect = async (value: any) => {
    if (value && value.id) {
      setInputValue(value.label);
      setValue('address', value.label);
      setValue('country', value.country || '');
      clearErrors('address');
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
        );

        const data = await response.json();

        if (data) {
          const { position, address } = data;

          if (position) {
            const { lat, lng: lon } = position;

            const location = {
              lat,
              lon,
            };

            setValue('location', location);
          }

          if (address) {
            setValue('country', address.countryName || '');
            setValue('postcode', address.postalCode || '');
          }
        }
      } catch (error) {
        console.error('Error fetching position information:', error);
      }
    }
  };

  const handleFetchTypedLocationSuggestions = (input: string, index: number) => {
    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: any;
              address: { label: string; city: string; countryName: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setLocationOptionsMap((prev) => ({ ...prev, [index]: items }));
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleTypedLocationSelect = async (value: any, index: number) => {
    if (!value) {
      setLocationInputMap((prev) => ({ ...prev, [index]: '' }));
      setValue(`locations.${index}.line1`, '');
      setValue(`locations.${index}.city`, '');
      setValue(`locations.${index}.country`, '');
      setValue(`locations.${index}.postalCode`, '');
      setValue(`locations.${index}.coordinates`, { lat: 0, lon: 0 });
      return;
    }

    if (value.id) {
      setLocationInputMap((prev) => ({ ...prev, [index]: value.label }));
      setValue(`locations.${index}.line1`, value.label);
      setValue(`locations.${index}.city`, value.city || '');
      setValue(`locations.${index}.country`, value.country || '');
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
        );

        const data = await response.json();

        if (data) {
          const { position, address } = data;

          if (position) {
            const { lat, lng: lon } = position;
            setValue(`locations.${index}.coordinates`, { lat, lon });
          }

          if (address) {
            setValue(`locations.${index}.city`, address.city || '');
            setValue(`locations.${index}.country`, address.countryName || '');
            setValue(`locations.${index}.postalCode`, address.postalCode || '');
          }
        }
      } catch (error) {
        console.error('Error fetching position information:', error);
      }
    }
  };

  return (
    <div className='flex flex-col space-y-6'>
      <section className='space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div>
          <h2 className='font-inter text-lg font-semibold leading-7 text-gray-900'>
            Customer details
          </h2>
          <p className='mt-0.5 font-inter text-sm text-gray-500'>
            Who this customer is, the person to reach for orders, and their head
            office.
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='customerId'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Customer ID'
                placeholder='Enter customer ID'
                isInvalid={!!formState.errors.customerId}
                validationMessage={formState.errors.customerId?.message}
              />
            )}
          />
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Customer Name'
                placeholder='Enter customer name'
                isInvalid={!!formState.errors.name}
                validationMessage={formState.errors.name?.message}
              />
            )}
          />
          <Controller
            name='type'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Customer Type'
                placeholder='e.g. Retailer, Wholesaler'
                isInvalid={!!formState.errors.type}
                validationMessage={formState.errors.type?.message}
              />
            )}
          />
          <Controller
            name='contactName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Name'
                placeholder='Enter contact name'
                isInvalid={!!formState.errors.contactName}
                validationMessage={formState.errors.contactName?.message}
              />
            )}
          />
          <Controller
            name='contactEmail'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Email'
                placeholder='name@company.com'
                type='email'
                isInvalid={!!formState.errors.contactEmail}
                validationMessage={formState.errors.contactEmail?.message}
              />
            )}
          />
          <Controller
            name='contactNumber'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Number'
                placeholder='Enter contact number'
                type='tel'
                optional
                isInvalid={!!formState.errors.contactNumber}
                validationMessage={formState.errors.contactNumber?.message}
              />
            )}
          />
          <Controller
            name='hqId'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='HQ ID'
                placeholder='Enter HQ ID'
                optional
                isInvalid={!!formState.errors.hqId}
                validationMessage={formState.errors.hqId?.message}
              />
            )}
          />
          <Controller
            name='hqName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='HQ Name'
                placeholder='Enter HQ name'
                optional
                isInvalid={!!formState.errors.hqName}
                validationMessage={formState.errors.hqName?.message}
              />
            )}
          />
        </div>
      </section>

      <section className='space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div>
          <h2 className='font-inter text-lg font-semibold leading-7 text-gray-900'>
            Delivery location
          </h2>
          <p className='mt-0.5 font-inter text-sm text-gray-500'>
            Where orders are delivered and the hours this site can receive them.
          </p>
        </div>
        <Controller
          name='address'
          control={control}
          render={({ field }) => (
            <Autocomplete
              label='Location'
              options={options}
              value={inputValue ?? field.value}
              onInputChange={(newInputValue) => {
                setInputValue(newInputValue);
                handleFetchLocationSuggestions(newInputValue);
              }}
              onChange={(value) => {
                handleLocationOptionSelect(value);
              }}
              isInvalid={!!formState.errors.address}
              validationMessage={formState.errors.address?.message as string}
            />
          )}
        />
        <div className='rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-700'>
          Pick the address from the list — we capture the{' '}
          <span className='font-medium'>postcode</span> and map coordinates
          automatically, ready for transport quotes.
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='operatingHoursFrom'
            control={control}
            render={({ field }) => (
              <Timepicker
                name={field.name}
                value={field.value}
                label='Operating Hours From'
                onChange={field.onChange}
                isInvalid={!!formState.errors.operatingHoursFrom}
              />
            )}
          />
          <Controller
            name='operatingHoursTo'
            control={control}
            render={({ field }) => (
              <Timepicker
                name={field.name}
                value={field.value}
                label='Operating Hours To'
                onChange={field.onChange}
                isInvalid={!!formState.errors.operatingHoursTo}
              />
            )}
          />
        </div>
        <Controller
          name='notes'
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              label='Notes'
              placeholder='Enter any additional notes'
              optional
            />
          )}
        />
      </section>

      <CollapsibleFormSection
        title='Classification & hierarchy'
        description='Master-data attributes that drive the customer table columns and account grouping.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='recordType'
            control={control}
            render={({ field }) => (
              <Select
                label='Record type'
                optional
                placeholder='Select record type'
                options={recordTypeOptions}
                selected={
                  recordTypeOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <Controller
            name='lifecycleStatus'
            control={control}
            render={({ field }) => (
              <Select
                label='Lifecycle status'
                optional
                placeholder='Select lifecycle status'
                options={lifecycleStatusOptions}
                selected={
                  lifecycleStatusOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <GroupTextField
            control={control}
            name='market'
            label='Market'
            placeholder='e.g. UK Grocery'
          />
          <GroupTextField
            control={control}
            name='channel'
            label='Channel'
            placeholder='e.g. Retail, Foodservice'
          />
          <GroupTextField
            control={control}
            name='partnerPayerRef'
            label='Partner payer ref'
          />
          <GroupTextField
            control={control}
            name='partnerBillToRef'
            label='Partner bill-to ref'
          />
        </div>
        <div className='rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-700'>
          Choose <span className='font-medium'>Group</span> for{' '}
          <span className='font-medium'>Bucket</span> and add a{' '}
          <span className='font-medium'>Group name</span> to file this customer
          under a shared account header; choose{' '}
          <span className='font-medium'>Independent</span> for a standalone
          account.
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='hierarchyBucket'
            control={control}
            render={({ field }) => (
              <Select
                label='Bucket'
                optional
                placeholder='Select bucket'
                options={hierarchyBucketOptions}
                selected={
                  hierarchyBucketOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <GroupTextField
            control={control}
            name='hierarchyGroupName'
            label='Group name'
          />
          <GroupTextField
            control={control}
            name='hierarchyParentName'
            label='Parent name'
          />
          <GroupTextField
            control={control}
            name='hierarchySubNodeName'
            label='Sub-node name'
          />
          <GroupTextField
            control={control}
            name='hierarchyBanner'
            label='Banner'
          />
          <Controller
            name='readinessGaps'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Readiness gaps'
                type='number'
                placeholder='e.g. 0'
                optional
                isInvalid={!!formState.errors.readinessGaps}
                validationMessage={formState.errors.readinessGaps?.message}
              />
            )}
          />
        </div>
        <div className='flex flex-wrap gap-6'>
          <GroupCheckbox
            control={control}
            name='hierarchyIsShipToSite'
            label='Ship-to site'
          />
          <GroupCheckbox
            control={control}
            name='hierarchyIsDemandNode'
            label='Demand node'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Legal entity'
        description='Registered company identifiers used for compliance and invoicing.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='legalEntity.legalName'
            label='Legal name'
          />
          <GroupTextField
            control={control}
            name='legalEntity.tradingName'
            label='Trading name'
          />
          <GroupTextField
            control={control}
            name='legalEntity.companyRegistrationNumber'
            label='Company registration number'
          />
          <GroupTextField
            control={control}
            name='legalEntity.vatNumber'
            label='VAT number'
          />
          <GroupTextField
            control={control}
            name='legalEntity.taxId'
            label='Tax ID'
          />
          <Controller
            name='legalEntity.jurisdictionCountry'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Jurisdiction country (ISO-2)'
                placeholder='e.g. GB'
                optional
                isInvalid={
                  !!formState.errors.legalEntity?.jurisdictionCountry
                }
                validationMessage={
                  formState.errors.legalEntity?.jurisdictionCountry?.message
                }
              />
            )}
          />
          <GroupTextField
            control={control}
            name='legalEntity.legalEntityType'
            label='Legal entity type'
          />
          <GroupTextField
            control={control}
            name='legalEntity.incorporationDate'
            label='Incorporation date'
            type='date'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Contacts'
        description='People to reach at this customer, with their role and linked site.'
      >
        <div className='space-y-4'>
          {contactFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeContact(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove contact'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Contact {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.name`}
                  label='Name'
                />
                <Controller
                  name={`contacts.${index}.email`}
                  control={control}
                  render={({ field: emailField }) => (
                    <Input
                      {...emailField}
                      value={emailField.value ?? ''}
                      label='Email'
                      type='email'
                      optional
                      isInvalid={
                        !!formState.errors.contacts?.[index]?.email
                      }
                      validationMessage={
                        formState.errors.contacts?.[index]?.email?.message
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.phoneCountryCode`}
                  label='Phone country code'
                  placeholder='e.g. +44'
                />
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.phoneNumber`}
                  label='Phone number'
                />
                <Controller
                  name={`contacts.${index}.role`}
                  control={control}
                  render={({ field: roleField }) => (
                    <Select
                      label='Role'
                      optional
                      placeholder='Select role'
                      options={contactRoleOptions}
                      selected={
                        contactRoleOptions.find(
                          (option) => option.value === roleField.value
                        ) ?? null
                      }
                      setSelected={(option) => roleField.onChange(option.value)}
                    />
                  )}
                />
                <Controller
                  name={`contacts.${index}.locationId`}
                  control={control}
                  render={({ field: locationField }) => (
                    <Select
                      label='Linked location'
                      optional
                      placeholder='Select location'
                      options={linkedLocationOptions}
                      selected={
                        linkedLocationOptions.find(
                          (option) => option.value === locationField.value
                        ) ?? null
                      }
                      setSelected={(option) =>
                        locationField.onChange(option.value)
                      }
                    />
                  )}
                />
              </div>
              <div className='mt-3'>
                <GroupCheckbox
                  control={control}
                  name={`contacts.${index}.isActive`}
                  label='Active contact'
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type='button'
          onClick={() =>
            appendContact({
              ...emptyCustomerContact,
              contactId: crypto.randomUUID(),
            })
          }
          className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
        >
          <PlusIcon className='h-4 w-4' />
          Add contact
        </button>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Additional locations'
        description='Extra registered, billing or shipping sites beyond the delivery location above.'
      >
        <div className='space-y-4'>
          {locationFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeLocation(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove location'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Location {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Controller
                  name={`locations.${index}.type`}
                  control={control}
                  render={({ field: typeField }) => (
                    <Select
                      label='Type'
                      optional
                      placeholder='Select type'
                      options={locationTypeOptions}
                      selected={
                        locationTypeOptions.find(
                          (option) => option.value === typeField.value
                        ) ?? null
                      }
                      setSelected={(option) => typeField.onChange(option.value)}
                    />
                  )}
                />
                <Controller
                  name={`locations.${index}.line1`}
                  control={control}
                  render={({ field: line1Field }) => (
                    <Autocomplete
                      label='Address line 1'
                      options={locationOptionsMap[index] || []}
                      value={locationInputMap[index] ?? line1Field.value}
                      onInputChange={(newInputValue) => {
                        setLocationInputMap((prev) => ({
                          ...prev,
                          [index]: newInputValue,
                        }));
                        setValue(`locations.${index}.line1`, newInputValue);
                        handleFetchTypedLocationSuggestions(
                          newInputValue,
                          index
                        );
                      }}
                      onChange={(value) => {
                        handleTypedLocationSelect(value, index);
                      }}
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.line2`}
                  label='Address line 2'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.city`}
                  label='City'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.county`}
                  label='County'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.postalCode`}
                  label='Postcode'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.country`}
                  label='Country'
                />
              </div>
              <div className='mt-3 flex flex-wrap gap-6'>
                <GroupCheckbox
                  control={control}
                  name={`locations.${index}.isPrimary`}
                  label='Primary location'
                />
                <GroupCheckbox
                  control={control}
                  name={`locations.${index}.isActive`}
                  label='Active location'
                />
              </div>
            </div>
          ))}
        </div>
        <div className='rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-700'>
          Pick each address from the list — we capture the{' '}
          <span className='font-medium'>postcode</span>, city and coordinates
          automatically.
        </div>
        <button
          type='button'
          onClick={() =>
            appendLocation({
              ...emptyCustomerTypedLocation,
              locationId: crypto.randomUUID(),
            })
          }
          className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
        >
          <PlusIcon className='h-4 w-4' />
          Add location
        </button>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Corporate hierarchy & commercial'
        description='Group structure and the commercial terms that govern this account.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='corporateHierarchy.parentCustomerId'
            label='Parent customer ID'
          />
          <GroupTextField
            control={control}
            name='corporateHierarchy.ultimateParentCustomerId'
            label='Ultimate parent customer ID'
          />
          <Controller
            name='corporateHierarchy.hierarchyLevel'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Hierarchy level'
                placeholder='e.g. 1'
                optional
                isInvalid={
                  !!formState.errors.corporateHierarchy?.hierarchyLevel
                }
                validationMessage={
                  formState.errors.corporateHierarchy?.hierarchyLevel?.message
                }
              />
            )}
          />
          <GroupTextField
            control={control}
            name='corporateHierarchy.regionalGrouping'
            label='Regional grouping'
          />
        </div>
        <GroupCheckbox
          control={control}
          name='corporateHierarchy.isGroupAccount'
          label='Group account'
        />
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='commercial.paymentTerms'
            label='Payment terms'
            placeholder='e.g. Net 30'
          />
          <Controller
            name='commercial.creditLimit'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Credit limit'
                placeholder='e.g. 50000'
                optional
                isInvalid={!!formState.errors.commercial?.creditLimit}
                validationMessage={
                  formState.errors.commercial?.creditLimit?.message
                }
              />
            )}
          />
          <Controller
            name='commercial.creditCurrency'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Credit currency (ISO-3)'
                placeholder='e.g. GBP'
                optional
                isInvalid={!!formState.errors.commercial?.creditCurrency}
                validationMessage={
                  formState.errors.commercial?.creditCurrency?.message
                }
              />
            )}
          />
          <GroupTextField
            control={control}
            name='commercial.contractType'
            label='Contract type'
          />
          <GroupTextField
            control={control}
            name='commercial.defaultIncoterms'
            label='Default incoterms'
          />
          <GroupTextField
            control={control}
            name='commercial.pricingTier'
            label='Pricing tier'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Compliance'
        description='KYC and sanctions screening status for this customer.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='compliance.kycStatus'
            label='KYC status'
          />
          <GroupTextField
            control={control}
            name='compliance.kycVerifiedAt'
            label='KYC verified at'
            type='date'
          />
          <GroupTextField
            control={control}
            name='compliance.sanctionsCheckStatus'
            label='Sanctions check status'
          />
          <GroupTextField
            control={control}
            name='compliance.sanctionsCheckedAt'
            label='Sanctions checked at'
            type='date'
          />
        </div>
      </CollapsibleFormSection>
    </div>
  );
}
